import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const LocationTimezone = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-GB', {
      timeZone: 'Europe/London',
      hour12: true,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getWorkingHoursStatus = () => {
    const now = new Date();
    const londonTime = new Date(now.toLocaleString("en-GB", {timeZone: "Europe/London"}));
    const hour = londonTime?.getHours();
    const day = londonTime?.getDay();
    
    // Monday = 1, Sunday = 0
    const isWeekday = day >= 1 && day <= 5;
    const isWorkingHours = hour >= 9 && hour < 18;
    
    if (isWeekday && isWorkingHours) {
      return { status: 'available', message: 'Currently available for calls (London time)', color: 'emerald' };
    } else if (isWeekday) {
      return { status: 'offline', message: 'Outside working hours (London time)', color: 'amber' };
    } else {
      return { status: 'weekend', message: 'Weekend - Limited availability', color: 'slate' };
    }
  };

  const workingStatus = getWorkingHoursStatus();

  const timezones = [
    { name: 'London (My Location)', timezone: 'Europe/London', offset: 'GMT/BST' },
    { name: 'New York', timezone: 'America/New_York', offset: 'EST/EDT' },
    { name: 'San Francisco', timezone: 'America/Los_Angeles', offset: 'PST/PDT' },
    { name: 'Tokyo', timezone: 'Asia/Tokyo', offset: 'JST' },
    { name: 'Sydney', timezone: 'Australia/Sydney', offset: 'AEST/AEDT' }
  ];

  const getTimezoneTime = (timezone) => {
    return new Date()?.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour12: true,
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Location & Availability */}
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8">Location & Availability</h2>
            
            {/* Current Status */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">Current Status</h3>
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
                  workingStatus?.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                  workingStatus?.color === 'amber'? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    workingStatus?.color === 'emerald' ? 'bg-emerald-500' :
                    workingStatus?.color === 'amber'? 'bg-amber-500' : 'bg-slate-500'
                  }`}></div>
                  <span>{workingStatus?.message}</span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Icon name="Clock" size={24} className="text-blue-600" />
                  <div>
                    <div className="text-2xl font-mono font-bold text-slate-900">{formatTime(currentTime)}</div>
                    <div className="text-sm text-slate-600">{formatDate(currentTime)} • Europe/London</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Icon name="MapPin" size={24} className="text-blue-600" />
                  <div>
                    <div className="font-semibold text-slate-900">London, United Kingdom</div>
                    <div className="text-sm text-slate-600">Greenwich Mean Time / British Summer Time (GMT/BST)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Working Hours */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Working Hours</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="font-medium text-slate-700">Monday - Friday</span>
                  <span className="text-slate-600">9:00 AM - 6:00 PM (London)</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="font-medium text-slate-700">Saturday</span>
                  <span className="text-slate-600">10:00 AM - 2:00 PM (London)</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-slate-700">Sunday</span>
                  <span className="text-slate-600">Emergency only</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Flexible for International Clients</p>
                    <p>I'm happy to accommodate calls outside regular hours for international projects and urgent matters.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map & Timezone Converter */}
          <div>
            {/* Interactive Map */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-xl font-bold text-slate-900">My Location</h3>
                <p className="text-slate-600">London, United Kingdom</p>
              </div>
              <div className="h-64 bg-slate-100">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="London Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=51.5074,-0.1278&z=12&output=embed"
                  className="border-0"
                ></iframe>
              </div>
            </div>

            {/* Timezone Converter */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">World Clock</h3>
              <p className="text-slate-600 mb-6">Current time in major cities around the world</p>
              
              <div className="space-y-4">
                {timezones?.map((tz, index) => (
                  <div key={index} className={`flex justify-between items-center p-4 rounded-lg transition-colors ${
                    index === 0 ? 'bg-blue-50 border-2 border-blue-200' : 'bg-slate-50'
                  }`}>
                    <div>
                      <div className={`font-semibold ${index === 0 ? 'text-blue-900' : 'text-slate-900'}`}>{tz?.name}</div>
                      <div className="text-sm text-slate-600">{tz?.offset}</div>
                    </div>
                    <div className={`text-lg font-mono font-bold ${index === 0 ? 'text-blue-700' : 'text-slate-700'}`}>{getTimezoneTime(tz?.timezone)}</div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Globe" size={20} className="text-emerald-600 mt-0.5" />
                  <div className="text-sm text-emerald-800">
                    <p className="font-medium mb-1">Remote Collaboration</p>
                    <p>I work with clients globally and am experienced in managing projects across different time zones.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationTimezone;
